// Atmospheric entry flight-state machine.
//
// This is the seam the game architecture grows through: fly mode asks
// this module "where am I relative to the nearest body's atmosphere"
// once per frame and gets back a flight state plus the continuous
// factors that drive presentation — craft scale (the flight-sim trick
// that makes an artistic-scale planet feel planet-sized as you
// descend), and sky occlusion (how much the daylit atmosphere should
// wash out the stars). Future stages — true-scale local bubble with a
// floating origin, streamed terrain, landing/walk mode — key off the
// same states without touching the orbital code paths.
//
// States:
//   SPACE        — outside the pilotable envelope; nothing changes.
//   ENTRY        — inside the envelope (between atmosphere top and
//                  ~1/3 depth); craft begins shrinking, sky begins
//                  brightening on the day side.
//   ATMOSPHERIC  — deep in the envelope; full scale illusion, daylit
//                  sky fully occludes the starfield.

import * as THREE from 'three';

export const FLIGHT_STATES = {
  SPACE: 'space',
  ENTRY: 'entry',
  ATMOSPHERIC: 'atmospheric'
};

// Earth flight uses a metre-sized craft and chase rig near the surface.
// Scaling both by exactly the same factor preserves angular size/framing.
// Other bodies retain their existing presentation scale.
export const ATMO_MIN_CRAFT_SCALE = 1.0;
export function earthFlightScale(radius) { return radius * 1000 / 6371000; }

const _zenith = new THREE.Vector3();
const _sunDir = new THREE.Vector3();

// Computes the entry state from the per-frame nearest-body sample
// (main.js findNearestBodySurface shape). `sunPos` defaults to the
// origin, where the sun lives in every system this sim renders.
// Writes into `out` to stay allocation-free in the frame loop.
export function computeAtmosphericEntry(out, nearest, cameraPos, sunPos = null) {
  out.state = FLIGHT_STATES.SPACE;
  out.immersion = 0;
  out.dayLight = 0;
  out.skyOcclusion = 0;
  out.craftScale = 1;
  out.body = null;

  if (!nearest || !nearest.hasAtmosphere || nearest.atmosphereDepth <= 0) return out;
  if (!(nearest.dist < nearest.atmosphereDepth)) return out;

  out.body = nearest.body;
  // 0 at the top of the pilotable envelope, 1 at the surface.
  const altitude01 = THREE.MathUtils.clamp(nearest.dist / nearest.atmosphereDepth, 0, 1);
  out.immersion = 1 - altitude01;
  out.state = out.immersion > 0.65 ? FLIGHT_STATES.ATMOSPHERIC : FLIGHT_STATES.ENTRY;

  // Sun elevation at the camera's footprint: zenith is the local up
  // (camera away from body centre); dayLight ramps in across the
  // terminator the same way the surface shaders do, so the sky only
  // hides stars where the atmosphere is actually sunlit. A night-side
  // entry keeps the stars — which is both correct and the better
  // visual.
  if (nearest.pos) {
    _zenith.copy(cameraPos).sub(nearest.pos).normalize();
    if (sunPos) _sunDir.copy(sunPos).sub(cameraPos).normalize();
    else _sunDir.copy(cameraPos).multiplyScalar(-1).normalize();
    const cosZenith = _zenith.dot(_sunDir);
    out.dayLight = THREE.MathUtils.smoothstep(cosZenith, -0.08, 0.22);
  }

  // Stars wash out with sunlit air mass above you. The 1.5 exponent
  // keeps high-altitude entry transparent (ISS height still shows
  // stars over the day side) and only fully occludes deep in.
  out.skyOcclusion = Math.pow(out.immersion, 1.5) * out.dayLight;

  // Scale illusion ramps in over the first 85% of descent so it is
  // complete before low-altitude flight, where it matters.
  const scaleEase = THREE.MathUtils.smoothstep(out.immersion, 0.0, 0.85);
  const earth = nearest.body?.name === 'earth';
  const minimum = earth ? earthFlightScale(nearest.radius) : ATMO_MIN_CRAFT_SCALE;
  out.craftScale = Math.exp(Math.log(minimum) * scaleEase);

  return out;
}

export function createAtmosphericEntryState() {
  return {
    state: FLIGHT_STATES.SPACE,
    immersion: 0,
    dayLight: 0,
    skyOcclusion: 0,
    craftScale: 1,
    body: null
  };
}
